'use client';

import type { ProductIndexData } from '@eliadmin/core/src/api/products/types/product-types';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { Highlight } from '@eliadmin/core/src/components/ui/highlight';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { SEARCH_CONFIG } from '@eliadmin/core/src/components/header/search-bar/search-config';
import { getNtProductPriceDisplay } from '@eliadmin/core/src/utils/nt/product-price';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { searchProductHref } from '@eliadmin/core/src/utils/search-drawer';
import type { SearchDrawerLabels } from '@eliadmin/core/src/i18n/search-drawer-labels';
import { FiArrowUpRight, FiImage } from 'react-icons/fi';
import styles from '@eliadmin/core/src/components/header/search-bar/search-drawer.module.css';

export function SearchDrawerProduct({
  product,
  query,
  labels,
  onNavigate,
}: {
  product: ProductIndexData;
  query: string;
  labels: SearchDrawerLabels;
  onNavigate: () => void;
}) {
  const layout = useGlobalStore(s => s.generalLayout);
  const withoutVat = useGlobalStore(
    s => s.templateSettings?.product_detail?.price_without_vat_first?.enabled,
  );
  const showUnit = useGlobalStore(s => s.templateSettings?.product_detail?.show_unit?.enabled);
  const showSecondary = useGlobalStore(
    s => s.templateSettings?.product_teaser?.price_without_vat?.enabled,
  );
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const price = getNtProductPriceDisplay(product, {
    priceWithoutVatFirst: withoutVat,
    isWholesaler,
  });
  const hidePrice = SEARCH_CONFIG.hideProductPrice?.(product) === true;
  const unit = showUnit && product.unit ? ` / ${product.unit}` : '';
  const href = searchProductHref(product);
  if (!href) return null;
  // The listing offer is not necessarily the matched variant's offer. Keep its image,
  // price and availability together; the destination can still select the matched variant.
  const from =
    product.has_variants || product.available_variants_count! > 1 || !!product.matched_variant_id;
  return (
    <Link
      href={href}
      prefetch={false}
      className={styles.product}
      onClick={onNavigate}
      data-search-result
    >
      <span className={styles.thumb}>
        {product.image?.url ? (
          <Image
            src={product.image.url}
            alt={resolveImageAlt(product.image, product.name, 'image')}
            width={76}
            height={76}
            sizes="76px"
          />
        ) : (
          <FiImage aria-hidden />
        )}
      </span>
      <span className={styles.productInfo}>
        {product.categories?.[0]?.name && (
          <span className={styles.productCategory}>{product.categories[0].name}</span>
        )}
        <span className={styles.productName}>
          <Highlight query={query} className={{ highlight: 'bg-primary/10 text-primary' }}>
            {product.name}
          </Highlight>
        </span>
        <span className={styles.productMeta}>
          {product.availability?.name && (
            <span
              style={{
                color: product.availability.color_text || undefined,
                backgroundColor: product.availability.color || undefined,
                borderRadius: 4,
                padding: '1px 4px',
              }}
            >
              {product.availability.name}
            </span>
          )}
          {product.code && <span>{product.code}</span>}
        </span>
      </span>
      {!hidePrice && (
        <span className={styles.productPrice}>
          {price.showMocVocPrices && price.mocPrice != null && (
            <small>
              MOC: {from && `${labels.from} `}
              {formatPrice(price.mocPrice, layout.currency, layout.currency_settings)}
              {unit}
            </small>
          )}
          <strong className="ea-search-drawer-price">
            {price.priceOnRequest ? (
              labels.onRequest
            ) : (
              <>
                {price.showMocVocPrices && 'VOC: '}
                {from && `${labels.from} `}
                {formatPrice(price.current, layout.currency, layout.currency_settings)}
                {unit}
              </>
            )}
          </strong>
          {!price.priceOnRequest && (
            <small>{withoutVat ? labels.withoutVat : labels.withVat}</small>
          )}
          {!price.priceOnRequest && showSecondary && price.secondary != null && (
            <small>
              {price.showMocVocPrices ? 'VOC: ' : ''}
              {formatPrice(price.secondary, layout.currency, layout.currency_settings)}
              {unit} {price.secondaryIncludesVat ? labels.withVat : labels.withoutVat}
            </small>
          )}
          {price.showPreviousPrice && (
            <del>
              {formatPrice(price.beforeDiscount, layout.currency, layout.currency_settings)}
            </del>
          )}
        </span>
      )}
      <FiArrowUpRight className={styles.arrow} aria-hidden />
    </Link>
  );
}
